import React, { useRef, useState, useEffect, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { FiLoader } from 'react-icons/fi';
import { makeStyles } from '@material-ui/core/styles';
import Pagination from '@material-ui/lab/Pagination';

import * as Yup from 'yup';
import { Form } from '@unform/web';
import { BsFilterLeft, BsFilter } from 'react-icons/bs';
import { Input } from '~/components/Forms';

import ApiClient from '~/utils/ApiClient';

import { PageContainer, TableHeader } from '~/styles/components';

import Aside from '~/components/Aside';
import { Button } from '~/components/global';

import { Container, Wrap, WrapButtons, TitleForm, Options } from './styles';

import {
  ListPageUsuarios,
  ListPageUsuarioSemCadastro,
} from '~/components/Admin';

import { showToastMessage } from '~/services/notification';
import buildQueryPackageFilters from '~/functions/buildQueryPackageFilters';
import maskString from '~/functions/maskString';
import { formatCnpj, normalizeCpf, normalizeCnpj } from '~/functions/document';
import WhiteBox from '~/components/WhiteBox';
import Option from '~/components/Option';
import { useList } from '~/hooks/useFunction';

/**
 * Todo campo já nasce com string vazia para o input ser controlado desde a
 * primeira renderização. Com `{}` o React ia de não-controlado a controlado no
 * primeiro change, e é nessa transição que o valor normalizado precisa chegar
 * à tela.
 */
const EMPTY_SEARCH_FORM = {
  created_at: { value: '' },
  name: { value: '' },
  cpf: { value: '' },
  email_filter: { value: '' },
  cnpj: { value: '' },
  company_name: { value: '' },
  fantasy_name: { value: '' },
};

const FIELD_NORMALIZERS = {
  cpf: normalizeCpf,
  cnpj: normalizeCnpj,
  email_filter: value => value.trim(),
};

/**
 * A API monta `LIKE '%valor%'`, onde `_` e `%` são curingas: buscar
 * `pimenta_jr@...` trazia também `pimenta.jr@...`. O `\` os torna literais —
 * é o escape padrão do MySQL, e a conexão (strict => false) não liga
 * NO_BACKSLASH_ESCAPES.
 */
const LIKE_WILDCARDS = /[\\%_]/g;

/**
 * O builder concatena o valor cru na URL. Em e-mail isso quebra: `+` chega ao
 * PHP como espaço e um endereço com alias (fulano+x@...) nunca casa. O encode
 * fica restrito ao e-mail para não mudar o que os outros filtros já enviam.
 */
function encodeEmailFilter(formData) {
  const emailFilter = formData.email_filter;
  if (!emailFilter?.value) return formData;

  const literalValue = emailFilter.value.replace(LIKE_WILDCARDS, '\\$&');

  return {
    ...formData,
    email_filter: { ...emailFilter, value: encodeURIComponent(literalValue) },
  };
}

/**
 * Trecho do filtro de e-mail na query montada por buildQueryPackageFilters
 * (o campo email_filter usa filterName="email" e o operador padrão like).
 */
const EMAIL_FILTER_IN_QUERY = /(filters\[\]\[email@like\]=)[^&]*/;

/**
 * Valor que entra no filtro. Documento é normalizado aqui, no change, e não
 * em onKeyPress: colagem não dispara evento de tecla, então o handler de
 * tecla deixava passar valor mascarado.
 */
function resolveFieldValue({ name, value, checked, type }) {
  if (type === 'checkbox') return checked;

  const normalize = FIELD_NORMALIZERS[name];
  if (normalize) return normalize(value);

  return value;
}

function Usuario({ data }) {
  const formRef = useRef();
  const cities = useList({ url: 'city?paginate=false' });
  const neighborhoods = useList({ url: 'neighborhood?paginate=false' });

  const tabType = [
    { label: 'Pessoa Física', value: 'user' },
    { label: 'Pessoa Jurídica', value: 'branch-admin' },
    { label: 'Sem cadastro', value: 'user-sem-cadastro' },
  ];

  const [hiddenButton, setHiddenButton] = useState(true);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totPage, setTotPage] = useState(1);
  const [item, setItem] = useState(0);

  const [userList, setUserList] = useState([]);
  const [filters, setFilters] = useState('');
  const [searchFormData, setSearchFormData] = useState(EMPTY_SEARCH_FORM);

  // --- pagination --- inicio
  const handleChangePaginator = (event, value) => {
    setPage(value);
  };

  const useStyles = makeStyles(theme => ({
    root: {
      '& > *': {
        marginTop: theme.spacing(2),
      },
    },
  }));

  function BasicPagination() {
    const classes = useStyles();
    return (
      <div className={classes.root}>
        <Pagination
          count={totPage}
          page={page}
          onChange={handleChangePaginator}
        />
      </div>
    );
  }
  // --- pagination --- fim

  /**
   * Só a resposta do pedido mais recente vai para a tela. Sem isso, trocar de
   * aba, buscar de novo ou mudar de página antes de a lista carregar deixava a
   * resposta antiga, quando chegava por último, sobrescrever a nova.
   */
  const latestRequestRef = useRef(0);

  const renderUsuarios = useCallback(async () => {
    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;
    const isStale = () => requestId !== latestRequestRef.current;

    try {
      setLoading(true);

      const resp = await ApiClient.doRequest({
        uri: `${tabType[item].value}?order[id]=desc&page=${page}${filters}`,
        method: ApiClient.GET,
        expectedStatus: 200,
      });
      if (isStale()) return;

      let userData = [];
      if (tabType[item].value === 'branch-admin') {
        userData = resp?.data?.data?.map(userPj => ({
          type: 'pj',
          uuid: userPj.uuid,
          created_at: userPj?.created_at,
          cpf_cnpj: formatCnpj(userPj.cnpj),
          name: userPj.company_name,
          files: userPj.files,
          fantasy_name: userPj.fantasy_name,
          phone: userPj.phone,
          cep: userPj?.cep,
          street: userPj?.street,
          neighborhood: userPj?.neighborhood?.name,
          number: userPj?.number,
          complement: userPj?.complement,
          city: userPj?.neighborhood?.city?.name,
          state: userPj?.neighborhood?.city?.state?.name,
          state_abbr: userPj?.neighborhood?.city?.state?.abbr,
        }));
      } else if (tabType[item].value === 'user-sem-cadastro') {
        userData = resp?.data?.data?.map(account => ({
          ...account,
          type: 'sem-cadastro',
        }));
      } else {
        userData = resp?.data?.data?.map(userPf => ({
          type: 'pf',
          uuid: userPf.uuid,
          created_at: userPf.user_personal_data?.created_at,
          cpf_cnpj: maskString(
            userPf.user_personal_data?.cpf?.toString().padStart(11, '0'),
            '###.###.###-##',
          ),
          name: userPf.user_personal_data?.name,
          files: userPf.files,
          email: userPf.email,
          rg: userPf.user_personal_data?.rg,
          phone_mobile: userPf.user_personal_data?.phone_mobile,
          drt: userPf.user_personal_data?.drt,
          drt_uf: userPf.user_personal_data?.uf,
          birth_date: userPf.user_birth_address?.birth_date,
          cep: userPf.user_address?.cep,
          street: userPf.user_address?.street,
          neighborhood: userPf.user_address?.neighborhood?.name,
          number: userPf.user_address?.number,
          complement: userPf.user_address?.complement,
          city: userPf.user_address?.neighborhood?.city?.name,
          state: userPf.user_address?.neighborhood?.city?.state?.name,
          state_abbr: userPf.user_address?.neighborhood?.city?.state?.abbr,
        }));
      }

      setTotPage(resp.data.meta.pagination.total_pages);
      setUserList(userData);
    } catch {
      if (isStale()) return;

      showToastMessage({
        type: 'error',
        text: `Não foi possível listar os usuários de ${tabType[item].label}`,
      });
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [filters, item, page]);

  useEffect(() => {
    renderUsuarios();
  }, [renderUsuarios]);

  const handleSubmit = useCallback(
    async _ => {
      try {
        formRef.current.setErrors({});

        setPage(1);
        setFilters(buildQueryPackageFilters(encodeEmailFilter(searchFormData)));
      } catch (err) {
        const validationErrors = {};
        if (err instanceof Yup.ValidationError) {
          err.inner.forEach(error => {
            validationErrors[error.path] = error.message;
          });
          formRef.current.setErrors(validationErrors);
        }
      }
    },
    [searchFormData],
  );

  /**
   * Depois de trocar o e-mail, recarregar com o filtro antigo esconderia a
   * conta editada ("Nenhum registro encontrado"). Se a busca aplicada filtra
   * por e-mail, só esse trecho passa a ser o e-mail novo. A decisão olha o
   * filtro aplicado, não o texto do campo: o campo pode ter sido editado ou
   * apagado sem clicar em Buscar.
   */
  const handleEmailSaved = useCallback(
    newEmail => {
      if (!EMAIL_FILTER_IN_QUERY.test(filters)) {
        renderUsuarios();
        return;
      }

      const encodedEmail = encodeEmailFilter({
        email_filter: { value: newEmail },
      }).email_filter.value;
      const updatedFilters = filters.replace(
        EMAIL_FILTER_IN_QUERY,
        (_, prefix) => `${prefix}${encodedEmail}`,
      );

      setSearchFormData(state => ({
        ...state,
        email_filter: { ...state.email_filter, value: newEmail },
      }));

      // Filtro igual não dispara o efeito de busca; recarrega direto.
      if (updatedFilters === filters) {
        renderUsuarios();
        return;
      }

      setPage(1);
      setFilters(updatedFilters);
    },
    [filters, renderUsuarios],
  );

  const renderList = useCallback(() => {
    if (loading) {
      return <FiLoader size={20} className="icon-spin" />;
    }

    if (!loading && !userList.length) {
      return 'Nenhum registro encontrado';
    }

    /**
     * A linha segue o tipo do item, não a aba: logo após a troca de aba a
     * lista ainda é a anterior por um render, até o novo pedido começar.
     */
    return (
      <>
        {userList.map(user => user.type === 'sem-cadastro' ? (
          <ListPageUsuarioSemCadastro key={user.uuid} data={user} onSaved={handleEmailSaved} />
        ) : (
          <ListPageUsuarios key={user.id} data={user} onClose={renderUsuarios} cities={cities} neighborhoods={neighborhoods} />
        ))}
      </>
    );
  }, [loading, userList, cities, neighborhoods, handleEmailSaved]);

  function handleResetSearchFields() {
    setPage(1);
    setFilters('');
    setSearchFormData(EMPTY_SEARCH_FORM);
  }

  function handleChange(e) {
    const { name } = e.target;

    setSearchFormData(state => ({
      ...state,
      [name]: {
        value: resolveFieldValue(e.target),
        filterName: e.target?.getAttribute('filterName'),
        filterOperator: e.target?.getAttribute('filterOperator'),
      },
    }));
  }

  return (
    <PageContainer>
      <Aside />
      <Container>
        <Wrap>
          <TitleForm>
            <h4>Usuários</h4>
          </TitleForm>
        </Wrap>
        <Options>
          {tabType.map(type => (
            <Option
              active={item === tabType.indexOf(type)}
              onClick={() => {
                setItem(tabType.indexOf(type));
                handleResetSearchFields();
              }}
            >
              {type.label}
            </Option>
          ))}
        </Options>
        <WhiteBox column maxHeight>
          {hiddenButton === true && (
            <span tooltip="Exibir filtro!">
              <button
                type="button"
                onClick={() => {
                  setShow(true);
                  setHiddenButton(false);
                }}
              >
                <BsFilterLeft color="#000" size={24} />
              </button>
            </span>
          )}
          {show === true && (
            <Form ref={formRef} onSubmit={handleSubmit}>
              <Wrap>
                <Input
                  type="date"
                  name="created_at"
                  label="Data de Cadastro"
                  value={searchFormData?.created_at?.value}
                  onChange={handleChange}
                  filterName={
                    tabType[item].value === 'user'
                      ? 'user_personal_data.created_at'
                      : 'created_at'
                  }
                />
                {tabType[item].value === 'user' && (
                  <>
                    {/*
                      Sem maxLength de propósito: o corte do DOM acontece antes
                      do onChange e mutila valor colado com máscara. Quem limita
                      é normalizeCpf, depois de tirar a pontuação.
                    */}
                    <Input
                      type="text"
                      name="cpf"
                      label="CPF"
                      value={searchFormData?.cpf?.value}
                      onChange={handleChange}
                      filterName="user_personal_data.cpf"
                      filterOperator="equal"
                    />
                    <Input
                      type="text"
                      name="name"
                      label="Nome"
                      value={searchFormData?.name?.value}
                      onChange={handleChange}
                      filterName="user_personal_data.name"
                    />
                  </>
                )}

                {['user', 'user-sem-cadastro'].includes(tabType[item].value) && (
                  <>
                    {/*
                      O name não é "email" de propósito: o Input usa o name
                      como id, e o modal de edição (ListPageUsuarios) já tem
                      um campo "email". Com os dois na tela, o label do modal
                      apontava para este filtro. A coluna consultada vem do
                      filterName.
                    */}
                    <Input
                      type="text"
                      name="email_filter"
                      label="E-mail"
                      value={searchFormData?.email_filter?.value}
                      onChange={handleChange}
                      filterName="email"
                    />
                  </>
                )}

                {tabType[item].value === 'branch-admin' && (
                  <>
                    {/* Sem maxLength e sem textTransform: normalizeCnpj já
                        corta a máscara e devolve em maiúsculas de verdade — o
                        uppercase por CSS não chegava no valor enviado. */}
                    <Input
                      type="text"
                      name="cnpj"
                      label="CNPJ"
                      value={searchFormData?.cnpj?.value}
                      onChange={handleChange}
                      filterOperator="equal"
                    />
                    <Input
                      type="text"
                      name="company_name"
                      label="Razão Social"
                      value={searchFormData?.company_name?.value}
                      onChange={handleChange}
                    />
                    <Input
                      type="text"
                      name="fantasy_name"
                      label="Nome Fantasia"
                      value={searchFormData?.fantasy_name?.value}
                      onChange={handleChange}
                    />
                  </>
                )}
              </Wrap>
              <Wrap>
                <WrapButtons>
                  <Button
                    type="submit"
                    btType="#26A2B1"
                    label="Buscar"
                    full
                    width="170px"
                  />
                  <Button
                    btType="#707683"
                    onClick={handleResetSearchFields}
                    label="Limpar"
                    full
                    width="100px"
                  />
                  <span tooltip="Ocultar filtro">
                    <button
                      type="button"
                      onClick={() => {
                        setShow(false);
                        setHiddenButton(true);
                      }}
                    >
                      <BsFilter color="#000" size={24} />
                    </button>
                  </span>
                </WrapButtons>
              </Wrap>
            </Form>
          )}
          <Wrap>
            <table>
              <TableHeader>
                {tabType[item].value === 'user-sem-cadastro' ? (
                  <tr>
                    <th>Data de Cadastro</th>
                    <th>E-mail</th>
                    <th>Ações</th>
                  </tr>
                ) : (
                  <tr>
                    <th>Data de Cadastro</th>
                    <th>{tabType[item].value === 'user' ? 'CPF' : 'CNPJ'}</th>
                    <th>
                      {tabType[item].value === 'user' ? 'Nome' : 'Razão Social'}
                    </th>
                    {tabType[item].value === 'branch-admin' && (
                      <th>Nome Fantasia</th>
                    )}
                    <th>Ações</th>
                  </tr>
                )}
              </TableHeader>
              <WhiteBox column aCenter>
                <tbody>{renderList()}</tbody>
                <BasicPagination />
              </WhiteBox>
            </table>
          </Wrap>
        </WhiteBox>
      </Container>
    </PageContainer>
  );
}

export default withRouter(Usuario);
